import { sqliteTable, text, real, integer, index } from "drizzle-orm/sqlite-core";

// ============================================================================
// purchase_matching_policies — PO/GR/PI tolerance policy per company
// ============================================================================
export const purchaseMatchingPolicies = sqliteTable("purchase_matching_policies", {
  id: text("id").primaryKey(),
  companyId: text("company_id").notNull(),
  name: text("name").notNull(),
  isDefault: integer("is_default").notNull().default(0),
  qtyTolerancePct: real("qty_tolerance_pct").notNull().default(0),
  qtyToleranceQty: real("qty_tolerance_qty").notNull().default(0),
  priceTolerancePct: real("price_tolerance_pct").notNull().default(0),
  priceToleranceAmount: real("price_tolerance_amount").notNull().default(0),
  taxToleranceAmount: real("tax_tolerance_amount").notNull().default(0),
  overReceiptAction: text("over_receipt_action", { enum: ["block", "warn", "allow"] })
    .notNull()
    .default("block"),
  overBillingAction: text("over_billing_action", { enum: ["block", "warn", "allow"] })
    .notNull()
    .default("block"),
  requireApproval: integer("require_approval").notNull().default(1),
  createdBy: text("created_by"),
  createdAt: text("created_at").notNull(),
  updatedBy: text("updated_by"),
  updatedAt: text("updated_at").notNull(),
}, (table) => ({
  policyCompanyIdx: index("pcm_company_idx").on(table.companyId, table.isDefault),
}));

// ============================================================================
// purchase_landed_costs — landed cost document header
// ============================================================================
export const purchaseLandedCosts = sqliteTable("purchase_landed_costs", {
  id: text("id").primaryKey(),
  landedCostNo: text("landed_cost_no").notNull().unique(),
  companyId: text("company_id").notNull(),
  branchId: text("branch_id"),
  postingDate: text("posting_date").notNull(),
  allocationMethod: text("allocation_method", { enum: ["amount", "qty", "equal"] })
    .notNull()
    .default("amount"),
  clearingAccountId: text("clearing_account_id").notNull(),
  totalAmount: real("total_amount").notNull().default(0),
  status: text("status", { enum: ["draft", "submitted", "cancelled"] }).notNull().default("draft"),
  remarks: text("remarks"),
  glEntryIds: text("gl_entry_ids"),
  createdBy: text("created_by"),
  createdAt: text("created_at").notNull(),
  updatedBy: text("updated_by"),
  updatedAt: text("updated_at").notNull(),
}, (table) => ({
  landedCompanyStatusIdx: index("plc_company_status_idx").on(table.companyId, table.status, table.postingDate),
}));

// ============================================================================
// purchase_landed_cost_lines — landed cost charge lines
// ============================================================================
export const purchaseLandedCostLines = sqliteTable("purchase_landed_cost_lines", {
  id: text("id").primaryKey(),
  landedCostId: text("landed_cost_id").notNull(),
  chargeType: text("charge_type").notNull(),
  description: text("description"),
  amount: real("amount").notNull().default(0),
  sortOrder: integer("sort_order").notNull().default(0),
  createdAt: text("created_at").notNull(),
}, (table) => ({
  landedLineDocIdx: index("plcl_landed_cost_idx").on(table.landedCostId),
}));

// ============================================================================
// purchase_landed_cost_allocations — allocation to GR item lines
// ============================================================================
export const purchaseLandedCostAllocations = sqliteTable("purchase_landed_cost_allocations", {
  id: text("id").primaryKey(),
  landedCostId: text("landed_cost_id").notNull(),
  goodsReceiptId: text("goods_receipt_id").notNull(),
  goodsReceiptItemId: text("goods_receipt_item_id").notNull(),
  itemId: text("item_id").notNull(),
  warehouseId: text("warehouse_id").notNull(),
  baseAmount: real("base_amount").notNull().default(0),
  allocatedAmount: real("allocated_amount").notNull().default(0),
  createdAt: text("created_at").notNull(),
}, (table) => ({
  landedAllocDocIdx: index("plca_landed_cost_idx").on(table.landedCostId),
  landedAllocReceiptIdx: index("plca_receipt_item_idx").on(table.goodsReceiptId, table.goodsReceiptItemId),
}));

export type PurchaseMatchingPolicy = typeof purchaseMatchingPolicies.$inferSelect;
export type NewPurchaseMatchingPolicy = typeof purchaseMatchingPolicies.$inferInsert;
export type PurchaseLandedCost = typeof purchaseLandedCosts.$inferSelect;
export type NewPurchaseLandedCost = typeof purchaseLandedCosts.$inferInsert;
export type PurchaseLandedCostLine = typeof purchaseLandedCostLines.$inferSelect;
export type NewPurchaseLandedCostLine = typeof purchaseLandedCostLines.$inferInsert;
export type PurchaseLandedCostAllocation = typeof purchaseLandedCostAllocations.$inferSelect;
export type NewPurchaseLandedCostAllocation = typeof purchaseLandedCostAllocations.$inferInsert;
